import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SwapVertOutlined } from '@mui/icons-material';
import { Checkbox, Stack, Typography } from '@mui/material';
import { axisClasses } from '@mui/x-charts';
import { BarChart } from '@mui/x-charts/BarChart';
import { ColorsV2 } from 'app/constants/color';
import apiService from 'app/services/apiService';

const GapBarChart = ({ fwId, reRender }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [chartData, setChartData] = useState({});
  const [sortOrder, setSortOrder] = useState(''); // This value is overridden immediately on component mount
  const [timeoutID, setTimeoutID] = useState();

  const maxBars = 6;

  const language = JSON.parse(localStorage.getItem('lang'));

  let res = null;
  const fetchSections = async () => {
    try {
      res = await apiService.getFramworkAnswersSummary(fwId, language);
      const sortedData = [...res.data.data].sort((a, b) => b.level - a.level);
      setSortOrder('ascending');
      setChartData({ ...res.data, data: sortedData });
      setIsLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchSections();

    // Timeout is not cleared when component unmounts to make sure sortOrder is saved even if user exits the current page
  }, [fwId, reRender]);

  // Sort order fetching should be refactored to make use of the new settings system (userSettingsSlice)

  // useEffect(() => {
  //   // Get sortOrder from settings on component mount
  //   if (
  //     JSON.parse(localStorage.getItem('user')) &&
  //     JSON.parse(JSON.parse(localStorage.getItem('user'))?.settings)?.gapBarchart?.sortOrder
  //   ) {
  //     setSortOrder(
  //       JSON.parse(JSON.parse(localStorage.getItem('user'))?.settings)?.gapBarchart?.sortOrder ||
  //         'descending'
  //     );
  //   }
  // }, []);

  const filterChart = (entry, index) => index < maxBars; // Limit displayed bars
  const valueFormatter = value => `${value}%`;

  const handleSort = () => {
    // Handle sorting of high-to-low or low-to-high
    const sortedData = [...chartData.data].sort((a, b) =>
      sortOrder === 'ascending' ? a.level - b.level : b.level - a.level
    );
    setSortOrder(sortOrder === 'ascending' ? 'descending' : 'ascending');
    setChartData(prevData => ({
      ...prevData,
      data: sortedData,
    }));

    if (
      !JSON.parse(localStorage.getItem('user')) ||
      !JSON.parse(JSON.parse(localStorage.getItem('user')).settings)
    ) {
      return;
    }
    const settings = JSON.parse(JSON.parse(localStorage.getItem('user')).settings);
    // Timeout function that saves the users chosen setting in 15 (can be adjusted) seconds. Resets if the user swaps sort order
    //
    // The timeout helps reduce unneccessary API calls. Some edge cases will prevent the setting from being saved.
    // For example logging out or closing the browser within 15 seconds of changing settings.
    // However since the setting is non-essential this seems like a worthwhile trade-off.
    clearTimeout(timeoutID);
    setTimeoutID(
      setTimeout(() => {
        settings.gapBarchart = {
          sortOrder,
        };
        const user = {
          users: [
            {
              ...JSON.parse(localStorage.getItem('user')),
              settings: JSON.stringify(settings),
            },
          ],
        };
        localStorage.setItem(
          'user',
          JSON.stringify({
            ...JSON.parse(localStorage.getItem('user')),
            settings: JSON.stringify(settings),
          })
        );
        apiService.updateUser(user); // Remove?
      }, 15000)
    );
  };

  const { t } = useTranslation('translation', { keyPrefix: 'gap_analysis' });

  return (
    isLoading ? (
      <>{t('loading')}</>
    ) : chartData.nrOfAnswered > 0 ? (
      <>
        <Typography
          variant='caption'
          color={ColorsV2.white}
          sx={{
            paddingRight: '5px',
            paddingLeft: '5px',
          }}
        >
          {chartData.description}
        </Typography>
        <Stack direction='row' alignItems='center'>
          <Checkbox
            icon={<SwapVertOutlined></SwapVertOutlined>}
            checkedIcon={<SwapVertOutlined></SwapVertOutlined>}
            disableRipple
            onChange={handleSort}
            size='small'
            sx={{
              color: ColorsV2.acent,
              margin: '5px',
              marginTop: '10px',
              marginBottom: '10px',
              '&.Mui-checked': {
                color: ColorsV2.acent,
              },
              '&:hover': {
                color: ColorsV2.white,
                bgcolor: ColorsV2.acent,
              },
            }}
          ></Checkbox>
          <Typography
            variant='body1'
            sx={{
              color: ColorsV2.textInfo,
            }}
          >
            {t('sorting')}{' '}
            {sortOrder === 'ascending' ? t('sort_high_to_low') : t('sort_low_to_high')}
          </Typography>
        </Stack>
        <BarChart
          dataset={chartData.data.filter(filterChart)}
          layout='horizontal'
          grid={{ vertical: true }}
          yAxis={[
            {
              scaleType: 'band',
              dataKey: 'name',
              tickLabelInterval: 'auto',
              margin: '10px',
              tickLabelStyle: {
                fill: ColorsV2.white,
                textAnchor: 'end',
                textWrap: 'wrap',
              },
            },
          ]}
          xAxis={[
            {
              max: 100,
              label: t('maturity') + ' %',
              valueFormatter: (level, context) =>
                context.location === 'tick' ? `${level}%` : `${level}`,
            },
          ]}
          series={[
            {
              dataKey: 'level',
              label: t('compliance'),
              color: ColorsV2.acent,
              valueFormatter,
            },
          ]}
          margin={{
            left: 400,
            right: 40,
            top: 20,
            bottom: 20,
          }}
          slotProps={{ legend: { hidden: true } }}
          tooltip={{ trigger: 'item' }}
          sx={{
            overflow: 'visible',
            color: ColorsV2.white,
            [`.${axisClasses.root}`]: {
              [`.${axisClasses.tick}, .${axisClasses.line}`]: {
                stroke: ColorsV2.acent,
                strokeWidth: 2,
              },
              [`.${axisClasses.tickLabel}`]: {
                fill: ColorsV2.white,
              },
              [`.${axisClasses.label}`]: {
                fill: ColorsV2.white,
              },
            },
          }}
        />
      </>
    ) : (
      <></>
    )
  );
};

export default GapBarChart;
